/**
 * The one place a request leaves this app.
 *
 * Two properties matter more than anything else here:
 *
 * **Same-origin, always.** Every path is relative, so the browser sends it to the
 * origin serving the bundle: in production FastAPI serves both, and in
 * development Vite proxies `/count`, `/auth` and `/health` to uvicorn. There is
 * therefore no base URL to configure, no origin to get wrong, and **no CORS
 * middleware on the backend at any point**.
 *
 * **Response text is never eagerly parsed into numbers.** `response.json()` keeps
 * the backend's decimal strings as strings, which is exactly what the PWA wants
 * (see `types.ts`). Nothing in this module coerces a field.
 */
import { DEV_USER_HEADER, readDevUpn, shouldSendDevHeader } from "../auth/devShim";

/**
 * A failed request, carrying the server's own words.
 *
 * The backend's `SessionError` path returns a `detail` string written for a
 * manager — "count session … is posting in Business Central and cannot be
 * cancelled" — so surfacing `detail` verbatim is better than any message this
 * layer could invent. `status` is kept because 403 (not your area), 409 (wrong
 * status) and 422 (BC refused) mean genuinely different things on screen.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly url: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

/** Set once `/auth/config` has answered. `null` means "not known yet". */
let serverAuthMode: string | null = null;

export function setServerAuthMode(mode: string | null): void {
  serverAuthMode = mode;
}

export function getServerAuthMode(): string | null {
  return serverAuthMode;
}

/**
 * How this module gets a bearer token, without knowing what MSAL is.
 *
 * A function rather than a value, and awaited per request, because the token
 * renews itself: MSAL hands back a cached access token until it is near expiry
 * and only then spends a round trip. A token *value* set here at sign-in would be
 * a copy with its own expiry, i.e. a second cache to get wrong.
 *
 * `null` means "no token available" and the request goes out unauthenticated so
 * the **server** decides what that means. Refusing locally would put this module
 * in the business of deciding who is signed in, which is `AuthContext`'s job and
 * ultimately the backend's.
 */
export type AccessTokenProvider = () => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider | null = null;

export function setAccessTokenProvider(provider: AccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

/**
 * Called when the API answers 401, with the server's own `detail`.
 *
 * The point is a screen, not a log line: a token that has expired or been refused
 * must land the manager back on S1 with a reason. Registered by `AuthProvider`
 * **only in `entra` mode** — dev mode's 401 (no header sent) is already visible
 * from the banner, and leaving that path untouched is how § 6.4's fences stay
 * exactly as they were.
 */
export type UnauthorizedHandler = (detail: string) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

async function authHeaders(): Promise<Record<string, string>> {
  // Fence 2 from § 6.4: the server's answer decides, not the bundle's belief.
  // This branch comes first so a production build folds it — and the header name
  // and the sessionStorage read with it — out of the bundle entirely.
  if (shouldSendDevHeader(serverAuthMode)) {
    const upn = readDevUpn();
    if (upn) return { [DEV_USER_HEADER]: upn };
  }
  // `entra`: the real path since D7. One place in the app writes this header.
  if (accessTokenProvider) {
    const token = await accessTokenProvider();
    if (token) return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(await authHeaders()),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const detail = await readDetail(response);
    // 401 is the one status this layer reports upwards as well as throwing: it
    // means the identity is gone, which is a whole-app fact rather than one
    // screen's error. 403 is deliberately not included — "not your area" is a
    // real answer from a real session, and signing someone out over it would
    // hide the message they need to read.
    if (response.status === 401) unauthorizedHandler?.(detail);
    throw new ApiError(response.status, detail, path);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * FastAPI's error body is `{"detail": …}`, but a 502 from nginx is HTML and a
 * dropped connection is nothing at all. All three have to produce something a
 * manager can read, so this never throws while building an error message.
 */
async function readDetail(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors arrive as a list of objects.
      return detail.map((d) => JSON.stringify(d)).join("; ");
    }
    return JSON.stringify(body);
  } catch {
    return `${response.status} ${response.statusText || "request failed"}`;
  }
}
